'use client';

import { DeleteConfirmDialog } from '@/components/account/common/DeleteConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/shadcn/dropdown-menu';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { InterviewStatus, Workspace } from '@/types/pulse';
import { Interview } from '@/types/pulsedb';
import { useWorkspace } from '@/utils/clerk/client';
import { EntitlementGate } from '@/components/entitlements/EntitlementGate';
import { updateInterviewStatus } from '@/utils/supabase/actions';
import { EllipsisIcon, EllipsisVerticalIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { mutate } from 'swr';
import InterviewDuplicateDialog from './InterviewDuplicateDialog';
import InterviewMoveDialog from './InterviewMoveDialog';

interface InterviewActionDropdownProps {
  interview: Interview;
  variant?: 'vertical' | 'horizontal';
  onDeleted?: (interviewId: number) => void;
  onDuplicated?: (newSlug: string) => void;
  cntConversations: number;
}

export default function InterviewActionDropdown({
  interview,
  variant = 'vertical',
  onDeleted,
  onDuplicated,
  cntConversations
}: InterviewActionDropdownProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const { toast } = useToast();
  const { workspace } = useWorkspace();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const toggleStatus = async (status: InterviewStatus) => {
    const toastMsgs: Record<InterviewStatus, string> = {
      [InterviewStatus.DRAFT]: `${interview.name} set to Draft`,
      [InterviewStatus.LIVE]: `${interview.name} now live`,
      [InterviewStatus.COMPLETE]: `${interview.name} set to Complete`,
      [InterviewStatus.ARCHIVED]: `${interview.name} archived`
    };
    await updateInterviewStatus(interview.id, status);
    toast({
      title: toastMsgs[status]
    });
    router.refresh();
  };

  const handleDeleted = () => {
    if (onDeleted) {
      return onDeleted(interview.id);
    }
  };

  const handleDuplicated = (newSlug: string) => {
    const recentInterviewsKey = workspace
      ? `/api/workspaces/${workspace.id}/recent-interviews`
      : null;
    if (recentInterviewsKey) {
      mutate(recentInterviewsKey);
    }

    const currentPath = pathname || '';
    if (currentPath.startsWith('/account/interview/')) {
      const currentParams = searchParams.toString();
      const parts = currentPath.split('/');
      const interviewIndex = parts.indexOf('interview');
      let nextPath = `/account/interview/${newSlug}`;

      if (interviewIndex !== -1 && parts.length > interviewIndex + 1) {
        parts[interviewIndex + 1] = newSlug;
        nextPath = parts.join('/');
      }

      const nextUrl = currentParams ? `${nextPath}?${currentParams}` : nextPath;
      router.push(nextUrl);
    }

    if (onDuplicated) {
      onDuplicated(newSlug);
    }
  };

  const handleMoved = async (workspace: Workspace) => {
    // Switch to the new workspace
    await workspace.setActive?.();

    toast({
      title: 'Interview moved successfully'
    });
    router.refresh();
  };

  const handleAdminView = () => {
    if (interview.id) {
      window.open(`/admin/interviews/show/${interview.id}`, '_blank');
    }
  };

  const IconComponent =
    variant === 'vertical' ? EllipsisVerticalIcon : EllipsisIcon;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="inline-flex items-center">
            <IconComponent size={18} className="text-zinc-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem asChild>
            <Link href={`/account/interview/${interview.slug}/edit`}>
              Configure
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setDuplicateDialogOpen(true)}>
            Duplicate
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
            Move
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)}>
            Delete
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          {interview.status === InterviewStatus.DRAFT && (
            <DropdownMenuItem
              onClick={() => toggleStatus(InterviewStatus.LIVE)}
            >
              Go Live
            </DropdownMenuItem>
          )}
          {interview.status === InterviewStatus.LIVE &&
            (cntConversations ?? 0) > 0 && (
              <DropdownMenuItem
                onClick={() => toggleStatus(InterviewStatus.COMPLETE)}
              >
                Close Interview
              </DropdownMenuItem>
            )}
          {interview.status === InterviewStatus.LIVE &&
            cntConversations === 0 && (
              <DropdownMenuItem
                onClick={() => toggleStatus(InterviewStatus.DRAFT)}
              >
                Reset to Draft
              </DropdownMenuItem>
            )}
          {interview.status === InterviewStatus.COMPLETE && (
            <DropdownMenuItem
              onClick={() => toggleStatus(InterviewStatus.LIVE)}
            >
              Set to Live
            </DropdownMenuItem>
          )}

          <EntitlementGate entitlement="admin" fallback={null}>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleAdminView()}>
              Admin View
            </DropdownMenuItem>
          </EntitlementGate>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleted}
        title="Delete Interview"
        description="Are you sure you want to delete this interview?"
        successToast={{
          title: 'Interview deleted'
        }}
      />

      <InterviewDuplicateDialog
        interview={interview}
        onDuplicated={handleDuplicated}
        duplicateDialogOpen={duplicateDialogOpen}
        setDuplicateDialogOpen={setDuplicateDialogOpen}
      />

      <InterviewMoveDialog
        interview={interview}
        onMoved={handleMoved}
        moveDialogOpen={moveDialogOpen}
        setMoveDialogOpen={setMoveDialogOpen}
      />
    </>
  );
}
